import React, { useEffect, useRef, useState } from 'react';
import { SuggestComboBox, SuggestComboBoxProps } from '../AbstractComboBox/SuggestComboBox';
import { TopContentResult } from '../AbstractComboBox/types/SuggestResultTypes';
import { useHtmlValidId } from '../../../../../../hooks/useHtmlValidId';
import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import { Row } from '../../../../../../_lib/Form2';
import getContentTopResults from './getContentTopResult';
import ListItemPage from '../AbstractComboBox/ListItemPage/ListItemPage';
import bem from '../../../../../../lib/bemUtils/bem';
import { PositionInSelectionInformation, SkipLinkTo } from '../../FormsComboboxMultiSelect/partials/CommonSkipLinks';
import { GroupPreview } from '../GroupPreview/GroupPreview';
import { Namespace } from '../../../../../../translation/namespace';
import { sendLiveRegionMessage } from '../../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';

interface GroupPreviewMultiSelectComboBoxForForm2Props
  extends Omit<SuggestComboBoxProps<TopContentResult>, 'id' | 'onSelection' | 'validationMessage'> {
  valueKey?: string;
  hasReadProtectedItems: boolean;
  value: TopContentResult[] | undefined;
}

/**
 * Form2 compatible ContentMultiSelectComboBox that allows to render "GroupPreview"s.
 * As with most combo boxes the name of the input should not be set.
 * Here we use the name as the hidden input for the selected page.
 * The ComboBox is responsible to set the hidden parameter, HTML conform JSON payload.
 */
export function GroupPreviewMultiSelectComboBoxForForm2({
  value,
  required,
  name,
  hasReadProtectedItems,
  ...otherProps
}: Readonly<GroupPreviewMultiSelectComboBoxForForm2Props>) {
  const id = useHtmlValidId();
  const { t } = useSonTranslation('FormsComboBox');
  const [results, setResults] = useState<TopContentResult[] | undefined>(value ?? []);
  const [deleteMessage, setDeleteMessage] = useState('');
  const suggestComboBoxRef = useRef<HTMLInputElement | null>(null);

  const LABEL_ID = 'forms-combobox-multiselect-label';

  useEffect(() => {
    if (deleteMessage) {
      sendLiveRegionMessage(deleteMessage);
    }
  }, [deleteMessage]);

  return (
    <Row>
      <div className="forms-combobox-multiselect__container">
        <SuggestComboBox
          {...otherProps}
          ref={suggestComboBoxRef}
          name={undefined} // need to overwrite name here after we spread props !
          id={`content-select-combobox-${id}`}
          responseMapper={getContentTopResults}
          onSelection={(item) => {
            if (item && results?.every(i => i.id !== item.id)) {
              setResults([item as TopContentResult, ...(results as TopContentResult[])]);
            }
          }}
          validationMessage={required && results?.length === 0 ? t('content.validationMessage') : undefined}
          required={required}
        >
          {item => <ListItemPage page={item} />}
        </SuggestComboBox>
        {results && results.length > 0 && (
          <div className="forms-combobox-multiselect__resultlist">
            <div className="forms-combobox-multiselect__label-container">
              <h3 id={LABEL_ID} className="forms-combobox-multiselect__label">
                {t(`${Namespace.FormsComboBoxMultiSelect}:resultlist.selectionHeadline`)}
              </h3>
            </div>
            {results.length >= 5 && (
              <>
                <PositionInSelectionInformation position="top" />
                <SkipLinkTo target="bottom" />
              </>
            )}
            <ul className={bem('forms-combobox-multiselect__list')} aria-labelledby={LABEL_ID}>
              {results.map((result, index) => (
                <GroupPreview
                  key={result.id}
                  id={`recipient-${index}-${result.id}`}
                  title={result.title}
                  name={name}
                  index={index}
                  result={result}
                  reset={() => {
                    setResults(results?.filter(element => element.id !== result.id));
                    setDeleteMessage(t(`${Namespace.FormsComboBoxMultiSelect}:deleted`, { item: result.title }));
                    suggestComboBoxRef.current?.focus();
                  }}
                />
              ))}
            </ul>
            {hasReadProtectedItems && (
              <li key="multi-select-hidden-items-info" className="forms-combobox-multiselect__list-item">
                <p className="forms-combobox-multiselect__title">
                  {t(`${Namespace.FormsComboBoxMultiSelect}:resultlist.hasHiddenItems`)}
                </p>
              </li>
            )}
            {results.length >= 5 && (
              <>
                <PositionInSelectionInformation position="bottom" />
                <SkipLinkTo target="top" />
              </>
            )}
          </div>
        )}
      </div>
    </Row>
  );
}
